import { firestore } from '../../plugins/firebase'
import {store} from '../../store'
import {deleteCropFromStoreCrops, deleteCropFromStoreDevices, deleteCropFromStoreField} from './storeOperations'
import {addDeleteChangeLog} from './write'
import get from 'lodash/get'


async function removeUserFromDevice (devRef, userId) {
  const dev = await devRef.get()
  const {users: devUsers} = dev.data()
  devUsers.data = devUsers.data.filter(user => user.id !== userId)
  Object.keys(devUsers.permissions.notifications).forEach(noti => {
    devUsers.permissions.notifications[noti] = devUsers.permissions.notifications[noti].filter(user => user.id !== userId)
  })
  return devRef.update({users: devUsers})
}

/**
 * Unlinks the user from the organization and devices (does not delete the user document)
 * @param {string} userId
 * @returns {Promise<Object>}
 */
export async function deleteUser (userId) {
  try {
    const db = await firestore
    const promises = []
    const activeCampaign = store.getters.activeCampaign

    // get user's campaign from userCampaigns subcollection
    const userRef = db.collection('app/v1/users').doc(userId)
    const userDoc = await userRef.get()
    if (userDoc.exists) {
      await addDeleteChangeLog(userRef, userDoc.data(), activeCampaign)
    }

    const userCampaignRef = db.collection(`app/v1/users/${userId}/campaigns`).doc(activeCampaign)
    const userCampaign = await userCampaignRef.get()
    const devices = userCampaign.data().devices || []

    // Remove user from devices
    devices.forEach(devRef => {
      promises.push(removeUserFromDevice(devRef, userId))
    })

    // Delete user's campaign from userCampaigns subcollection
    promises.push(userCampaignRef.delete())
    // Remove user from campaign users array
    const campaignRef = db.collection('app/v1/campaigns').doc(store.getters.activeCampaign)
    const campaignDoc = await campaignRef.get()
    const campaignData = campaignDoc.data()
    if (campaignData.users && Array.isArray(campaignData.users)) {
      // Remove user from campaign's users array
      const updatedUsers = campaignData.users.filter(user => {
        // Handle both object references and string IDs
        if (typeof user === 'object' && user.id) {
          return user.id !== userId
        }
        return user !== userId
      })
      
      promises.push(campaignRef.update({
        users: updatedUsers
      }))
    }
    const storeCampaign = store.getters.getCampaign(store.getters.activeCampaign)
    store.commit('updateCampaign', {
      campaignId: store.getters.activeCampaign,
      campaignData: {
        ...storeCampaign,
        users: storeCampaign.users.filter(user => user.id !== userId)
      }
    })

    const storeUsers = store.getters.users
    store.commit('setUsers', storeUsers.filter(user => user.id !== userId))

    // Remove user from devices in store
    const newDevices = store.getters.devices.map(device => {
      const users = device.users.filter(user => user.id !== userId)
      const notifications = device.notifications.filter(user => user.id !== userId)
      return { ...device, users, notifications }
    })
    store.commit('setDevices', newDevices)

    await Promise.all(promises)
  } catch (err) {
    console.error('ERROR_DELETE_USER:', err)
    throw err
  }
}

/**
 * Unlinks the crop from all its devices and fields (logical delete)
 * @param {string} cropId
 * @returns {Promise<Object>}
 */
export async function deleteCrop (cropId) {
  try {
    const db = await firestore
    const promises = []

    // Get crop data to find all related devices and fields
    const cropRef = db.collection('app/v1/crops').doc(cropId)
    const cropDoc = await cropRef.get()
    
    if (!cropDoc.exists) {
      throw new Error('Crop not found')
    }

    const cropData = cropDoc.data()

    await addDeleteChangeLog(cropRef, cropData, store.getters.activeCampaign)

    const deviceRefs = get(cropData, 'devices', [])
    const fieldRef = get(cropData, 'field')

    // Remove crop from all related devices
    deviceRefs.forEach(deviceRef => {
      promises.push(removeCropFromDevice(deviceRef, cropId))
    })

    // Remove crop from field if exists
    if (fieldRef) {
      promises.push(removeCropFromField(fieldRef, cropId))
    }

    // Update crop
    promises.push(cropRef.update({
      devices: [],
      field: null,
      organization: null
    }))

    await Promise.all(promises)
    
    // Update store
    deleteCropFromStoreField(cropId)
    deleteCropFromStoreDevices(cropId)
    deleteCropFromStoreCrops(cropId)
  } catch (err) {
    console.error('ERROR_DELETE_CROP:', err)
    throw err
  }
}

// Helper function to remove crop from device
async function removeCropFromDevice (deviceRef, cropId) {
  const db = await firestore
  return db.runTransaction(async (transaction) => {
    const deviceDoc = await transaction.get(deviceRef)
    if (!deviceDoc.exists) {
      return
    }
    
    const deviceData = deviceDoc.data()
    const campaign = deviceData.campaign || {}
    const crops = campaign.crops || []
    
    // Remove the crop from device's campaign
    const updatedCrops = crops.filter(crop => crop.cropRef !== cropId)
    
    return transaction.update(deviceRef, {
      campaign: {
        ...campaign,
        crops: updatedCrops
      }
    })
  })
}

// Helper function to remove crop from field
async function removeCropFromField (fieldRef, cropId) {
  const db = await firestore
  return db.runTransaction(async (transaction) => {
    const fieldDoc = await transaction.get(fieldRef)
    if (!fieldDoc.exists) {
      return
    }
    
    const fieldData = fieldDoc.data()
    const crops = fieldData.crops || []
    
    // Remove the crop from field's crops array (crops are stored as references)
    const updatedCrops = crops.filter(cropRef => cropRef.id !== cropId)
    
    return transaction.update(fieldRef, {
      crops: updatedCrops
    })
  })
}

async function removeFieldFromCampaign (campaignRef, fieldId) {
  const db = await firestore
  return db.runTransaction(async (transaction) => {
    const campaignDoc = await transaction.get(campaignRef)
    if (!campaignDoc.exists) {
      return
    }
    
    const campaignData = campaignDoc.data()
    const fields = campaignData.fields || []
    
    // Remove field from campaign's fields array (fields are stored as references)
    const updatedFields = fields.filter(fieldRef => {
      // Handle both object references and string IDs
      if (typeof fieldRef === 'object' && fieldRef.id) {
        return fieldRef.id !== fieldId
      }
      return fieldRef !== fieldId
    })
    
    return transaction.update(campaignRef, {
      fields: updatedFields
    })
  })
}

function removeCropsFromDevicesStore (cropsToDelete) {
  let newDevices = store.getters.devices
  if (cropsToDelete) {
    Object.entries(cropsToDelete).forEach(device => {
      let newCrops = []
      const deviceId = device[0]
      const cropIds = device[1]
      const deviceIndex = newDevices.findIndex(dev => dev.id === deviceId)
      if (deviceIndex !== -1) {
        newDevices[deviceIndex].campaign.crops.forEach(crop => {
          if (!cropIds.includes(crop.cropRef)) {
            newCrops.push(crop)
          }
        })
        newDevices[deviceIndex].campaign.crops = newCrops
      }
    })
  }
  store.commit('setDevices', newDevices)
}

function removeCropsFromCropsStore (cropsToDelete) {
  let crops = store.getters.crops
  let newCrops = []
  crops.forEach(crop => {
    if (!(Object.keys(cropsToDelete).includes(crop.device) && cropsToDelete[crop.device].includes(crop.id))) {
      newCrops.push(crop)
    }
  })
  store.commit('setCrops', newCrops)
}

function removeFieldFromFieldsStore (fieldId) {
  let fields = store.getters.fields
  fields = fields.filter(field => field.id !== fieldId)
  store.commit('setFields', fields)
}

async function removeCropsFromDevice (deviceRef, cropIds) {
  const deviceDoc = await deviceRef.get()
  let deviceData = deviceDoc.data()
  let crops = deviceData.campaign && deviceData.campaign.crops ? deviceData.campaign.crops.filter(crop => !cropIds.includes(crop.cropRef)) : []
  return deviceRef.update({
    campaign: {
      ...deviceData.campaign,
      crops
    }
  })
}

function removeFieldsFromFirestore (fieldRef) {
  return fieldRef.update({
    crops: []
  })
}

/**
 * Unlinks the field from the organization and each of its crops (logical delete)
 *
 * @param {string} fieldId
 * @returns {Promise<Object>}
 */
export async function deleteField (fieldId) {
  try {
    const db = await firestore
    const promises = []
    let cropsToDelete = {}

    const fieldRef = db.collection('app/v1/fields').doc(fieldId)
    const fieldDoc = await fieldRef.get()
    if (fieldDoc.exists) {
      await addDeleteChangeLog(fieldRef, fieldDoc.data(), store.getters.activeCampaign)
    }

    promises.push(removeFieldsFromFirestore(fieldRef))
    const devices = store.getters.devices
    devices.forEach(device => {
      if (device.campaign.crops) {
        const fieldCrops = []
        device.campaign.crops.forEach((crop, index) => {
          if (crop.field === fieldId) {
            fieldCrops.push(crop.cropRef || index)
          }
        })
        if (fieldCrops.length) {
          cropsToDelete[device.id] = fieldCrops
          const deviceRef = db.collection('app/v1/devices').doc(device.id)
          promises.push(removeCropsFromDevice(deviceRef, fieldCrops))
        }
      }
    })

    // Remove field from all campaigns
    const user = store.getters.user
    if (user && user.campaigns) {
      user.campaigns.forEach(campaign => {
        if (campaign.fields && Array.isArray(campaign.fields)) {
          const campaignRef = db.collection('app/v1/campaigns').doc(campaign.id)
          promises.push(removeFieldFromCampaign(campaignRef, fieldId))
        }
      })
    }

    await Promise.all(promises)

    removeCropsFromDevicesStore(cropsToDelete)
    removeCropsFromCropsStore(cropsToDelete)
    removeFieldFromFieldsStore(fieldId)
  } catch (err) {
    console.error('ERROR_DELETE_FIELD:', err)
    throw err
  }
}



// WEBPACK FOOTER //
// ./src/lib/crudOperations/delete.js